import React from 'react';
import { getTrackBackground, Range } from 'react-range';
import { SkeletonLoading } from '@saleshandy/design-system';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';
import type { DialerCreditSliderProps } from './dialer-credit-slider-container';
import type { DialerCreditSliderItem } from './types';

interface CreditSliderProps {
  value: number[];
  onChange: (v: number[]) => void;
  steps: DialerCreditSliderItem[];
}

const CreditSlider: React.FC<CreditSliderProps> = ({
  value,
  onChange,
  steps,
}) => {
  const min = 0;
  const max = steps.length - 1;

  const getMarkText = (index: number) => steps[index]?.label || '';

  const getMarkMargin = (index: number) => {
    if (index === 0) return '5px';
    if (index === max) return '-6px';
    return '0px';
  };

  return (
    <div
      className="range"
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        width: 'inherit',
      }}
    >
      <Range
        values={value}
        step={1}
        min={min}
        max={max}
        rtl={false}
        onChange={onChange}
        renderMark={({ props, index }) => (
          <div
            {...props}
            className="range__mark"
            style={{
              ...props.style,
              height: '4px',
              width: '4px',
              borderRadius: '50px',
              backgroundColor: index * 1 < value[0] ? '#2563EB' : '#275DF5',
              marginTop: '1.5px',
              marginLeft: getMarkMargin(index),
              transition: 'background-color 0.3s ease',
            }}
          >
            <div
              className="range__mark-text"
              style={{ marginLeft: getMarkMargin(index) }}
            >
              {getMarkText(index)}
            </div>
          </div>
        )}
        renderTrack={({ props, children }) => (
          <div
            role="none"
            onMouseDown={props.onMouseDown}
            onTouchStart={props.onTouchStart}
            className="range__track"
            style={{
              ...props.style,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: '8px',
              background: getTrackBackground({
                values: value,
                colors: ['#2563EB', '#DBEAFE'],
                min,
                max,
                rtl: false,
              }),
              borderRadius: '4px',
              position: 'relative',
              paddingRight: '3px',
              transition: 'background 0.3s ease',
            }}
          >
            <div
              ref={props.ref}
              className="range__track-ch"
              style={{ width: '100%', height: '100%' }}
            >
              {children}
            </div>
          </div>
        )}
        renderThumb={({ props }) => (
          <div
            {...props}
            className={
              (value[0] === min && 'range__thumb-first') ||
              (value[0] === max && 'range__thumb-last') ||
              ''
            }
            style={{
              ...props.style,
              height: '24px',
              width: '24px',
              borderRadius: '50px',
              outlineStyle: 'solid',
              outlineColor: '#275DF5',
              outlineWidth: '2px',
              backgroundColor: '#FFF',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0px 2px 6px #AAA',
              transition: 'transform 0.2ms ease',
            }}
          />
        )}
      />
    </div>
  );
};

const DialerCreditSlider: React.FC<DialerCreditSliderProps> = ({
  creditSliderItems,
  creditSliderValue,
  setCreditSliderValue,
  isLoading,
  onBuyNow,
  isBuyNowLoading = false,
  isBuyNowDisabled = false,
}) => {
  const selectedCreditStep =
    creditSliderItems.length > 0
      ? creditSliderItems[creditSliderValue[0]] || null
      : null;

  return (
    <div className="credit-slider-container">
      <div className="credit-slider-container-header">
        <div className="credit-slider-container-header-title-wrapper">
          <span className="credit-slider-container-header-title">
            Add More Credits
          </span>
          <span className="credit-slider-container-header-description">
            Add calling credits to connect with more prospects, close deals
            faster, and keep every conversation going without interruption.
          </span>
        </div>
        <Button
          variant={Button.Variant.Primary}
          theme={Button.Theme.Solid}
          className="solid-primary-new"
          onClick={onBuyNow}
          isLoading={isBuyNowLoading}
          disabled={isBuyNowDisabled || isLoading || !selectedCreditStep}
        >
          Buy Now
        </Button>
      </div>
      <div className="credit-slider-wrapper">
        {isLoading ? (
          <div className="credit-slider-loading">
            <div className="credit-slider-selected-value-wrapper">
              <SkeletonLoading width={120} height={20} />
              <SkeletonLoading width={60} height={20} />
            </div>
            <SkeletonLoading width="100%" height={8} />
          </div>
        ) : (
          <>
            <div className="credit-slider-selected-value-wrapper">
              <div className="credit-slider-selected-value">
                <span className="font-weight-bold">
                  {selectedCreditStep
                    ? `${selectedCreditStep.label} Credits`
                    : `${creditSliderItems[0]?.label || '0'} Credits`}
                </span>
              </div>
              <span className="credit-slider-selected-price">
                {selectedCreditStep
                  ? `$${selectedCreditStep.price}`
                  : `$${creditSliderItems[0]?.price || 0}`}
              </span>
            </div>
            {creditSliderItems.length > 0 && (
              <CreditSlider
                value={creditSliderValue}
                onChange={setCreditSliderValue}
                steps={creditSliderItems}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DialerCreditSlider;
